import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useCollectionData } from "../hooks/useCollectionData";
import { useSectionVisibility } from "../hooks/useSectionVisibility";
import { PACKAGES as FALLBACK_PACKAGES } from "../data/content";

const slug = (s) =>
    s
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");

export default function Packages() {
    const visibility = useSectionVisibility();
    const { data: packages } = useCollectionData("packages", FALLBACK_PACKAGES);

    if (!visibility.packages) return null;

    return (
        <section
            id="packages"
            data-testid="packages-section"
            className="relative bg-[#0a0908] py-28 sm:py-36 border-t border-[#d4af37]/10"
        >
            <div className="max-w-[1480px] mx-auto px-6 sm:px-10 lg:px-16">
                <div className="text-center max-w-3xl mx-auto mb-16">
                    <div className="eyebrow mb-6 justify-center">
                        Paket Pernikahan
                    </div>
                    <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#fdfbf7] font-light leading-[1.05] tracking-tight">
                        Dua tingkat
                        <span className="italic text-[#d4af37]"> kelengkapan</span>,
                        satu standar:
                        <span className="block">tanpa kompromi.</span>
                    </h2>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 max-w-5xl mx-auto">
                    {packages.map((p, i) => (
                        <motion.div
                            key={p.name || i}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ delay: i * 0.12, duration: 0.8 }}
                            data-testid={`package-${slug(p.name)}`}
                            className={`relative p-8 sm:p-10 flex flex-col ${
                                p.highlight
                                    ? "bg-gradient-to-b from-[#1a1816] to-[#0a0908] border border-[#d4af37] shadow-[0_0_50px_-12px_rgba(212,175,55,0.25)]"
                                    : "bg-[#15120f] border border-[#d4af37]/15"
                            }`}
                        >
                            {p.highlight && (
                                <div className="absolute top-0 right-8 -translate-y-1/2 bg-[#d4af37] text-[#0a0908] px-4 py-1.5 text-[10px] tracking-[0.3em] uppercase font-semibold">
                                    Paling Favorit
                                </div>
                            )}

                            <div className="mb-8">
                                <p className="text-[10px] tracking-[0.4em] uppercase text-[#d4af37] mb-3">
                                    {p.tier}
                                </p>
                                <h3 className="font-display text-3xl sm:text-4xl text-[#fdfbf7] mb-2 tracking-wide">
                                    {p.name}
                                </h3>
                                <p className="text-sm text-[#a6a196] italic">
                                    {p.tagline}
                                </p>
                            </div>

                            <div className="mb-8 pb-8 border-b border-[#d4af37]/15">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-xs tracking-[0.2em] text-[#a6a196]">
                                        Rp
                                    </span>
                                    <span className="font-display text-5xl sm:text-6xl text-[#fdfbf7]">
                                        {p.price}
                                    </span>
                                    <span className="text-base text-[#a6a196]">
                                        {p.unit}
                                    </span>
                                </div>
                                <p className="text-[10px] uppercase tracking-[0.25em] text-[#a6a196]/70 mt-1">
                                    Investasi · per acara
                                </p>
                            </div>

                            <ul className="flex-1 space-y-4 mb-10">
                                {p.features?.map((f, j) => (
                                    <li
                                        key={j}
                                        className="flex items-start gap-3 text-sm text-[#fdfbf7]/85"
                                    >
                                        <Check
                                            size={16}
                                            className="text-[#d4af37] mt-0.5 shrink-0"
                                            strokeWidth={1.5}
                                        />
                                        <span>{f}</span>
                                    </li>
                                ))}
                            </ul>

                            <button
                                data-testid={`select-package-${slug(p.name)}`}
                                onClick={() =>
                                    document
                                        .getElementById("contact")
                                        ?.scrollIntoView({ behavior: "smooth" })
                                }
                                className={`w-full py-4 text-xs tracking-[0.3em] uppercase transition-all duration-500 ${
                                    p.highlight
                                        ? "bg-[#d4af37] text-[#0a0908] hover:bg-[#f0c950]"
                                        : "border border-[#d4af37]/50 text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0908]"
                                }`}
                            >
                                Book Now · {p.name}
                            </button>
                        </motion.div>
                    ))}
                </div>

                <p className="mt-10 text-center text-xs tracking-[0.2em] uppercase text-[#a6a196]">
                    Tersedia paket kustom untuk destination wedding ·
                    Konsultasi gratis via WhatsApp
                </p>
            </div>
        </section>
    );
}
